import {CardType, ExecutionStatus, TaskType} from 'constants/dataEnum';
import {useArrowDownPress, useArrowUpPress} from 'lib/customHooks';
import {useDispatch, useSelector} from 'react-redux';
import {getDeckLabwareRefs} from 'stores/selectors/virtualTraySelectors';
import {getTaskIdExecutionStatus} from 'stores/business/runtimeBusiness';
import {mergeRefs} from 'lib/refHelper';
import React, {useRef} from 'react';
import {draggable, droppable} from 'lib/dragAndDrop';
import TaskDelay from 'contents/ProtocolEditor/TaskList/Task/TaskDelay';
import TaskConfirmation from 'contents/ProtocolEditor/TaskList/Task/TaskConfirmation';
import TaskAirGap from 'contents/ProtocolEditor/TaskList/Task/TaskAirGap';
import PropTypes from 'prop-types';

import {getCardById} from 'stores/selectors/cardStateSelector';
import TaskPickTip from './TaskPickTip';
import TaskTransfer from './TaskTransfer';
import TaskEjectTip from './TaskEjectTip';
import InsertTask from './InsertTask';
import MixingTask from '../../Mixing/Tasks/MixingTask';

// TODO: handle drag and drop flow. make wrapper component for all tasks
const SimpleTransferTasks = React.forwardRef(
  (
    {
      task,
      cardId,

      runtimeMode,
      readOnly,
      isMoreThanTipCapacity,
      ejectTipTasksExists,

      drop,
      drag,
      dragPreview,
      isDragging,
      disableDrag,

      onSelectTask,
      onArrowDownPress,
      onArrowUpPress,
      onDelete,
      onCheckedChange,
      onApply,

      excludedComponentRef,
      onShowMenuContext,
      menuListRef,
      editablePickTipExcludedRef,
      // onCloseEditablePickTip,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const card = useSelector(getCardById(cardId));

    const {ID, number, isIgnored, isSelected} = task;

    const deckLabwareRefs = useSelector(getDeckLabwareRefs);

    const taskRef = useRef(null);

    useArrowUpPress(taskRef, onArrowUpPress);
    useArrowDownPress(taskRef, onArrowDownPress);

    const executionStatus = runtimeMode ? dispatch(getTaskIdExecutionStatus(ID)) : ExecutionStatus.NONE;

    const onDeleteWithID = () => onDelete(ID);

    // TODO: Need to optimize drag n drop props;
    const draggingProperties = {
      drop,
      onDrag: drag,
      isDragging,
      disableDrag,
    };

    const taskCommonProperties = {
      ...draggingProperties,
      task,
      onCheckedChange,
      onDelete: onDeleteWithID,
      runtimeMode,
      readOnly,
      executionStatus,
      onSelectTask,
    };

    const onClickWrapper = (e) => {
      onSelectTask(ID, {cardType: CardType.SIMPLE_TRANSFER});

      // if (task.type === TaskType.PICK_TIP) {
      //   onCloseEditablePickTip();
      // }

      e.stopPropagation();
    };

    return (
      <div
        className="wrapper"
        ref={mergeRefs(drop, dragPreview, taskRef, ref)}
        onClick={onClickWrapper}
        role="presentation"
      >
        {task.type === TaskType.PICK_TIP && (
          <TaskPickTip
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...taskCommonProperties}
            excludedComponentRef={editablePickTipExcludedRef}
            ejectTipTasksExists={ejectTipTasksExists}
          />
        )}
        {(task.type === TaskType.DISPENSE || task.type === TaskType.ASPIRATE) && (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <TaskTransfer {...taskCommonProperties} onShowMenuContext={onShowMenuContext} cardId={cardId} />
        )}
        {task.type === TaskType.MIXING && (
          <MixingTask
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...taskCommonProperties}
            excludedComponentRef={[...Object.values(deckLabwareRefs ?? {}), ...excludedComponentRef]}
            card={card}
            onShowMenuContext={onShowMenuContext}
            onDelete={(e, isShowConfirmationModal) => onDelete(e, isShowConfirmationModal)}
          />
        )}
        {task.type === TaskType.EJECT_TIP && (
          <TaskEjectTip
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...taskCommonProperties}
            isMoreThanTipCapacity={isMoreThanTipCapacity}
          />
        )}
        {task.type === TaskType.DELAY && (
          <TaskDelay
            ID={ID}
            number={number}
            isIgnored={isIgnored}
            onCheckedChange={onCheckedChange}
            initialTask={task}
            onApply={onApply}
            onDelete={onDelete}
            runtimeMode={runtimeMode}
            executionStatus={executionStatus}
            drop={drop}
            drag={drag}
            dragPreview={dragPreview}
            isDragging={isDragging}
            disableDrag={disableDrag}
            isHighlightedTask={isSelected}
            excludedComponentRef={excludedComponentRef}
            isViewMode={readOnly}
            onSelectTask={onSelectTask}
          />
        )}
        {(task.type === TaskType.USER_CONFIRMATION || task.type === TaskType.USER_CONFIRMATION_EJECT) && (
          <TaskConfirmation
            ID={ID}
            number={number}
            isIgnored={isIgnored}
            onCheckedChange={onCheckedChange}
            initialTask={task}
            onApply={onApply}
            onDelete={onDelete}
            runtimeMode={runtimeMode}
            executionStatus={executionStatus}
            drop={drop}
            drag={drag}
            dragPreview={dragPreview}
            isDragging={isDragging}
            disableDrag={disableDrag}
            isHighlightedTask={isSelected}
            excludedComponentRef={excludedComponentRef}
            isViewMode={readOnly}
            onSelectTask={onSelectTask}
          />
        )}
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        {task.type === TaskType.AIR_GAP && <TaskAirGap cardId={cardId} {...taskCommonProperties} />}
        {task.type === TaskType.INSERT_TASK && (
          <InsertTask
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...taskCommonProperties}
            onDelete={(e, isShowConfirmationModal) => onDelete(e, isShowConfirmationModal)}
            excludedComponentRef={[...Object.values(deckLabwareRefs), ...excludedComponentRef, menuListRef]}
          />
        )}
      </div>
    );
  },
);

SimpleTransferTasks.propTypes = {
  task: PropTypes.object.isRequired,
  cardId: PropTypes.string,

  onDelete: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onCheckedChange: PropTypes.func.isRequired,
  onSelectTask: PropTypes.func.isRequired,
  onArrowDownPress: PropTypes.func.isRequired,
  onArrowUpPress: PropTypes.func.isRequired,

  runtimeMode: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  isMoreThanTipCapacity: PropTypes.bool.isRequired,
  ejectTipTasksExists: PropTypes.bool.isRequired,

  drop: PropTypes.any,
  drag: PropTypes.func,
  dragPreview: PropTypes.func,
  disableDrag: PropTypes.bool,
  isDragging: PropTypes.bool,

  excludedComponentRef: PropTypes.array,
  onShowMenuContext: PropTypes.func,
  menuListRef: PropTypes.any.isRequired,
  editablePickTipExcludedRef: PropTypes.array,
  // onCloseEditablePickTip: PropTypes.func,
};

SimpleTransferTasks.defaultProps = {
  drop: () => {},
  drag: () => {},
  dragPreview: () => {},
  disableDrag: false,
  isDragging: false,
  excludedComponentRef: [],
  onShowMenuContext: () => {},
  editablePickTipExcludedRef: [],
  cardId: null,
  // onCloseEditablePickTip: () => {},
};

export default draggable(droppable(SimpleTransferTasks));
